/**
 * 認証と権限判定の唯一の入口。
 * Firebase Auth のログイン状態と users/{uid} のロールをまとめて提供する。
 * 権限判定のロジックはここに集約し、画面側で個別に role を見比べない。
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  browserPopupRedirectResolver,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { getDb, getFirebaseAuth } from '@core/lib/firebase';
import type { Role, UserDoc } from '@core/lib/types';
import { claimInvite } from '@core/masters/userRepo';

/** loading: 判定中 / signed-out: 未ログイン / no-profile: users未登録 / inactive: 無効化 / ready: 利用可 / error: 権限の読込に失敗(通信など) */
export type AuthStatus = 'loading' | 'signed-out' | 'no-profile' | 'inactive' | 'ready' | 'error';

type AuthContextValue = {
  status: AuthStatus;
  user: UserDoc | null;
  firebaseUser: FirebaseUser | null;
  signIn: (email: string, password: string) => Promise<void>;
  /** 管理者・閲覧者のログイン(2026-09-15〜)。初回は招待(user_invites)から users を作る */
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  /** users/{uid} を読み直す(管理者が権限を変えた直後など) */
  refreshUser: () => Promise<void>;
  /** status が error のとき、権限の読込をやり直す(2026-10-05) */
  retryProfile: () => Promise<void>;

  // --- 権限判定(firestore.rules と同じ条件をUI側にも実装する) ---
  role: Role | null;
  isAdmin: boolean;
  isViewer: boolean;
  isOperator: boolean;
  /** ログイン直後・トップ(/)アクセス時の行き先 */
  homePath: string;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/** ロール別の初期画面(§3: admin=全機能 / viewer=閲覧 / operator=本人ページ) */
export function homePathFor(role: Role | null): string {
  switch (role) {
    case 'admin':
      return '/home';
    case 'viewer':
      return '/pl?mode=month';
    case 'operator':
      return '/staff';
    default:
      return '/';
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<UserDoc | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  /** Google ログインの処理中は、招待の受け取りが終わるまで権限の判定を待つ(「権限が未設定」を一瞬出さない) */
  const googleSigningIn = useRef(false);

  const loadProfile = useCallback(async (fbUser: FirebaseUser) => {
    const snap = await getDoc(doc(getDb(), 'users', fbUser.uid));
    if (!snap.exists()) {
      setUser(null);
      setStatus('no-profile');
      return;
    }
    const data = { id: snap.id, ...snap.data() } as UserDoc;
    setUser(data);
    setStatus(data.active ? 'ready' : 'inactive');
  }, []);

  useEffect(() => {
    return onAuthStateChanged(getFirebaseAuth(), async (fbUser) => {
      setFirebaseUser(fbUser);
      if (!fbUser) {
        setUser(null);
        setStatus('signed-out');
        return;
      }
      if (googleSigningIn.current) return; // signInWithGoogle が続けて判定する
      // 通信の失敗などで読めなかったら「読み込み中…」のまま止めず、案内とやり直しを出す(2026-10-05 全端末対応)
      try {
        await loadProfile(fbUser);
      } catch (e) {
        console.error('[cc-system] 権限の読込に失敗しました', e);
        setStatus('error');
      }
    });
  }, [loadProfile]);

  const refreshUser = useCallback(async () => {
    const fb = getFirebaseAuth().currentUser;
    if (fb) await loadProfile(fb);
  }, [loadProfile]);

  const signIn = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const auth = getFirebaseAuth();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' }); // 複数の Google アカウントを使う人が選べるように
    googleSigningIn.current = true;
    try {
      // initializeAuth で作っているので、ポップアップ用の部品をここで渡す(渡さないと auth/argument-error)
      const { user: fb } = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
      try {
        await claimInvite(fb);
        await loadProfile(fb);
      } catch (e) {
        await firebaseSignOut(auth); // 権限の確認まで終わらなければログインさせない
        throw e;
      }
    } finally {
      googleSigningIn.current = false;
    }
  }, [loadProfile]);

  const retryProfile = useCallback(async () => {
    const fb = getFirebaseAuth().currentUser;
    if (!fb) {
      setStatus('signed-out');
      return;
    }
    setStatus('loading');
    try {
      await loadProfile(fb);
    } catch (e) {
      console.error('[cc-system] 権限の読込に失敗しました', e);
      setStatus('error');
    }
  }, [loadProfile]);

  const signOut = useCallback(async () => {
    await firebaseSignOut(getFirebaseAuth());
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const role = user?.role ?? null;
    return {
      status,
      user,
      firebaseUser,
      signIn,
      signInWithGoogle,
      signOut,
      refreshUser,
      retryProfile,
      role,
      isAdmin: role === 'admin',
      isViewer: role === 'viewer',
      isOperator: role === 'operator',
      homePath: homePathFor(role),
    };
  }, [status, user, firebaseUser, signIn, signInWithGoogle, signOut, refreshUser, retryProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth は AuthProvider の内側で使ってください');
  return ctx;
}

/** ログイン済み前提の画面で使う */
export function useCurrentUser(): UserDoc {
  const { user } = useAuth();
  if (!user) throw new Error('ログインユーザーが未取得です(AuthGate の内側で使ってください)');
  return user;
}
